import { useEffect, useState } from "react";
import { SheetModeSelect } from "./PlanModePicker";
import { Loader2, Move, RotateCcw, Save, X, PlayCircle } from "lucide-react";
import { GcodeSim, fmtTime } from "./GcodeSim";
import { ColorSwatch } from "./ColorSwatch";
import { api } from "../lib/api";

// estimated CNC time per sheet; refreshed whenever the plan changes
const useCncTime = (projectId, result) => {
  const [t, setT] = useState(null);
  useEffect(() => {
    if (!projectId || !result) { return; }
    let alive = true;
    setT(null);
    api.get(`/projects/${projectId}/gcode-time`).then((r) => { if (alive) setT(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [projectId, result]);
  return t;
};
import { toast } from "sonner";
import { Button } from "./ui/button";
import { SheetEditor } from "./SheetEditor";
import { SHEET, THICK_BADGE, localEdgeSegs, outlinePath, pieceFeatures, pieceLabel } from "../lib/pieces";

const SL = SHEET.length, SW = SHEET.width;
const hue = (i) => `hsl(${(i * 47) % 360} 45% 32%)`;

function edgeSegs(pl, p) {
  return localEdgeSegs(p).map(([a, b, c, d]) => [...toSheet(pl, p, a, b), ...toSheet(pl, p, c, d)]);
}

function toSheet(pl, p, x, y) {
  return pl.rotated ? [pl.x + +p.width - y, pl.y + x] : [pl.x + x, pl.y + y];
}

function toSheetRect(pl, p, [rx, ry, rw, rh]) {
  return pl.rotated ? [pl.x + +p.width - ry - rh, pl.y + rx, rh, rw] : [pl.x + rx, pl.y + ry, rw, rh];
}

const fy = (y, h = 0) => SL - y - h;

const SheetSvg = ({ sheet, pieces }) => (
  <svg data-testid={`sheet-svg-${sheet.number}`} viewBox={`-20 -20 ${SW + 40} ${SL + 40}`} className="w-full h-auto">
    <defs>
      <pattern id="hatch" width="40" height="40" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2="40" stroke="#4B5563" strokeWidth="8" />
      </pattern>
    </defs>
    <rect x={0} y={0} width={SW} height={SL} fill="#3A3226" stroke="#CA7C38" strokeWidth={6} />
    <rect x={SHEET.trim} y={SHEET.trim} width={SW - 2 * SHEET.trim} height={SL - 2 * SHEET.trim} fill="none" stroke="#6B7280" strokeWidth={3} strokeDasharray="20 12" />
    {sheet.sobras.map((s, i) => (
      <g key={`s${i}`}>
        <rect x={s.x} y={fy(s.y, s.h)} width={s.w} height={s.h} fill="url(#hatch)" stroke="#6B7280" strokeWidth={3} opacity={0.8} />
        {s.w > 220 && s.h > 70 && (
          <text x={s.x + s.w / 2} y={fy(s.y, s.h) + s.h / 2} textAnchor="middle" dominantBaseline="middle" fontSize={Math.min(46, s.h / 3)} fill="#D1D5DB" fontFamily="JetBrains Mono">
            S{i + 1} · {s.w}x{s.h}
          </text>
        )}
      </g>
    ))}
    {sheet.placed.map((pl) => {
      const p = pieces[pl.idx];
      if (!p) return null;
      const { holes, grooves, leds, drills } = pieceFeatures(p);
      const fs = Math.max(18, Math.min(60, Math.min(pl.w, pl.h) / 4));
      return (
        <g key={pl.num} data-testid={`placed-piece-${pl.num}`}>
          <title>{`${pieceLabel(pl.num)} · ${p.name} · ${p.length} x ${p.width} mm${pl.rotated ? "" : " (girada)"}`}</title>
          <path fillRule="evenodd" d={outlinePath(p, (x, y) => { const [sx, sy] = toSheet(pl, p, x, y); return [sx, fy(sy)]; })} fill={hue(pl.idx)} stroke="#F3F4F6" strokeWidth={3} />
          {grooves.map((g, i) => {
            const [gx, gy, gw, gh] = toSheetRect(pl, p, g);
            return <rect key={i} x={gx} y={fy(gy, gh)} width={gw} height={gh} fill="#00E5FF" opacity={0.7} />;
          })}
          {leds.map((g, i) => {
            const [gx, gy, gw, gh] = toSheetRect(pl, p, g);
            return <rect key={`l${i}`} x={gx} y={fy(gy, gh)} width={gw} height={gh} fill="#FACC15" opacity={0.75} />;
          })}
          {holes.map(([hx, hy], i) => {
            const [sx, sy] = toSheet(pl, p, hx, hy);
            return <circle key={i} cx={sx} cy={fy(sy)} r={17.5} fill="none" stroke="#00E5FF" strokeWidth={4} />;
          })}
          {drills.map(([dx, dy], i) => {
            const [sx, sy] = toSheet(pl, p, dx, dy);
            return <circle key={`d${i}`} cx={sx} cy={fy(sy)} r={6} fill="#00E5FF" />;
          })}
          {edgeSegs(pl, p).map(([x1, y1, x2, y2], i) => (
            <line key={i} x1={x1} y1={fy(y1)} x2={x2} y2={fy(y2)} stroke="#E67E22" strokeWidth={12} />
          ))}
          {pl.w > 90 && pl.h > 50 && (
            <text x={pl.x + pl.w / 2} y={fy(pl.y, pl.h) + pl.h / 2} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontFamily="JetBrains Mono" fontWeight="700" fontSize={fs}>
              {pieceLabel(pl.num)}
              {pl.h > 140 && pl.w > 200 && (
                <tspan x={pl.x + pl.w / 2} dy={fs * 1.1} fontSize={fs * 0.7} fontWeight="400" fill="#E5E7EB">{p.length}x{p.width}</tspan>
              )}
            </text>
          )}
        </g>
      );
    })}
  </svg>
);

const EditBar = ({ result, editMode, saving, onStart, onCancel, onSave, onReset }) => (
  <div data-testid="plan-edit-bar" className="flex flex-wrap items-center gap-2 rounded-lg border border-[#2D333F] bg-[#181B21] p-3">
    <div className="text-sm mr-auto min-w-0">
      {result.manual && <p data-testid="plan-manual-notice" className="text-[#FACC15]">Plano ajustado manualmente — DXF, PDF e etiquetas usam este plano.</p>}
      {result.manual_stale && <p data-testid="plan-stale-notice" className="text-[#F39C12]">As peças ou o tipo de otimização mudaram depois do ajuste manual. O plano automático voltou a ser usado.</p>}
      {!result.manual && !result.manual_stale && <p className="text-[#9CA3AF]">Plano automático. Você pode mover e girar as peças à mão.</p>}
    </div>
    {editMode ? (
      <>
        <Button size="sm" variant="ghost" data-testid="plan-edit-cancel" onClick={onCancel} disabled={saving}><X className="w-4 h-4 mr-1" /> Cancelar</Button>
        <Button size="sm" data-testid="plan-edit-save" onClick={onSave} disabled={saving} className="bg-[#10B981] hover:bg-[#059669] text-black font-semibold">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Salvar ajustes
        </Button>
      </>
    ) : (
      <>
        {(result.manual || result.manual_stale) && <Button size="sm" variant="outline" data-testid="plan-reset-auto" onClick={onReset} disabled={saving} className="border-[#2D333F] bg-transparent"><RotateCcw className="w-4 h-4 mr-1" /> Voltar ao automático</Button>}
        <Button size="sm" data-testid="plan-edit-start" onClick={onStart} className="bg-[#E67E22] hover:bg-[#D35400] text-black font-semibold"><Move className="w-4 h-4 mr-1" /> Editar plano</Button>
      </>
    )}
  </div>
);

export const CutPlan = ({ result, pieces, loading, error, canEdit, onSaveLayout, onResetLayout, sheetModes = {}, onSheetMode, simProjectId }) => {
  const [sim, setSim] = useState(null);
  const cnc = useCncTime(simProjectId, result);
  const [edits, setEdits] = useState(null);
  const [saving, setSaving] = useState(false);
  if (loading) return <div data-testid="plan-loading" className="flex items-center gap-2 text-[#9CA3AF] p-10"><Loader2 className="w-5 h-5 animate-spin" /> Otimizando chapas…</div>;
  if (error) return <div data-testid="plan-error" className="rounded-lg border border-red-900 bg-red-950/40 p-4 text-red-300">{error}</div>;
  if (!result) return null;
  const run = async (fn) => {
    setSaving(true);
    try {
      await fn();
      setEdits(null);
    } catch (e) {
      toast.error(e.message || "Não foi possível salvar o plano.");
    } finally {
      setSaving(false);
    }
  };
  const saveEdits = () => run(() => onSaveLayout(result.groups.flatMap((g) => g.sheets).map((s) => ({
    number: s.number, placed: (edits[s.number] || s.placed).map(({ num, x, y, rotated }) => ({ num, x, y, rotated })),
  }))));
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          ["Chapas necessárias", result.total_sheets, "plan-total-sheets"],
          ["Chapa (vertical)", `${SW} x ${SL}`, "plan-sheet-size"],
          ["Refilo / espaço (fresa 6mm)", `${SHEET.trim} / ${String(SHEET.kerf).replace(".", ",")} mm`, "plan-trim-kerf"],
          ...(simProjectId ? [["Tempo total de CNC (estimado)", cnc ? fmtTime(cnc.total) : "…", "plan-cnc-time"]] : []),
        ].map(([l, v, t]) => (
          <div key={t} className="rounded-lg border border-[#2D333F] bg-[#181B21] px-4 py-3">
            <p className="font-mono text-[10px] uppercase tracking-wider text-[#6B7280]">{l}</p>
            <p data-testid={t} className="font-heading text-2xl font-bold mt-0.5">{v}</p>
          </div>
        ))}
      </div>
      <div data-testid="plan-sheets-by-model" className="rounded-lg border border-[#2D333F] bg-[#181B21] p-4">
        <p className="font-heading font-semibold mb-3">Chapas necessárias por modelo</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {result.groups.map((g, i) => (
            <div key={i} data-testid={`sheets-model-${i}`} className="flex items-center justify-between gap-3 rounded-md border border-[#2D333F] bg-[#0F1115] px-3 py-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`rounded border px-1.5 py-0.5 font-mono text-[11px] ${THICK_BADGE[g.thickness]}`}>{g.thickness}mm</span>
                <span className="truncate text-sm inline-flex items-center gap-1.5"><ColorSwatch name={g.mdf_color} />MDF {g.mdf_color}</span>
              </div>
              <span className="font-heading text-xl font-bold text-[#E67E22] whitespace-nowrap">{g.sheets.length} <span className="text-xs font-normal text-[#9CA3AF]">chapa{g.sheets.length > 1 ? "s" : ""}</span></span>
            </div>
          ))}
        </div>
      </div>
      {result.edge_band.length > 0 && (
        <div data-testid="plan-edge-summary" className="rounded-lg border border-[#2D333F] bg-[#181B21] p-4">
          <p className="font-heading font-semibold mb-2">Fita de borda necessária</p>
          <div className="flex flex-wrap gap-2">
            {result.edge_band.map((e) => (
              <span key={e.color} className="rounded-full border border-[#E67E22]/50 bg-[#E67E22]/10 px-3 py-1 text-sm">
                {e.color}: <b className="font-mono">{e.meters.toFixed(2)} m</b> <span className="text-[#9CA3AF] text-xs">(+10%: {e.meters_with_loss.toFixed(2)} m)</span>
              </span>
            ))}
          </div>
        </div>
      )}
      {canEdit && (
        <EditBar result={result} editMode={!!edits} saving={saving} onStart={() => setEdits({})} onCancel={() => setEdits(null)}
          onSave={saveEdits} onReset={() => run(onResetLayout)} />
      )}
      <div className="flex flex-wrap gap-4 text-xs text-[#9CA3AF]">
        <span className="flex items-center gap-1.5"><span className="w-5 h-1.5 bg-[#E67E22] rounded" /> Fita de borda</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full border-2 border-[#00E5FF]" /> Furo dobradiça Ø35</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#00E5FF]" /> Furo Ø8</span>
        <span className="flex items-center gap-1.5"><span className="w-5 h-1.5 bg-[#00E5FF] rounded" /> Canal fundo 6,5mm</span>
        <span className="flex items-center gap-1.5"><span className="w-5 h-1.5 bg-[#FACC15] rounded" /> Canal LED</span>
        <span className="flex items-center gap-1.5"><span className="w-4 h-3 bg-[repeating-linear-gradient(45deg,#4B5563_0_2px,transparent_2px_5px)] border border-[#6B7280]" /> Sobra aproveitável</span>
      </div>
      {result.groups.map((g, gi) => (
        <section key={gi} data-testid={`plan-group-${gi}`} className="space-y-4">
          <div className="flex items-center gap-3">
            <span className={`rounded border px-2 py-0.5 font-mono text-xs ${THICK_BADGE[g.thickness]}`}>{g.thickness}mm</span>
            <h3 className="font-heading text-lg font-semibold inline-flex items-center gap-2"><ColorSwatch name={g.mdf_color} size={16} testId={`group-swatch-${gi}`} />MDF {g.mdf_color}</h3>
            <span className="text-sm text-[#6B7280]">{g.piece_count} peças · {g.sheets.length} chapa(s)</span>
          </div>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {g.sheets.map((s) => (
            <div key={s.number} data-testid={`sheet-card-${s.number}`} className="rounded-xl border border-[#2D333F] bg-[#181B21] p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-heading font-semibold">Chapa {s.number} <span className="text-[#6B7280] font-normal">de {result.total_sheets}</span></p>
                <div className="flex flex-wrap items-center gap-3">
                  <SheetModeSelect number={s.number} value={sheetModes[s.number] || s.mode || result.plan_mode} onChange={onSheetMode} />
                  <span className="font-mono text-sm"><span data-testid={`sheet-usage-${s.number}`} className="text-[#10B981] font-bold">{s.usage.toFixed(2).replace(".", ",")}%</span> aproveitado · {s.placed.length} peças</span>
                  {simProjectId && cnc?.sheets[s.number] != null && (
                    <span data-testid={`sheet-cnc-time-${s.number}`} className="font-mono text-sm text-[#00E5FF]">CNC ~{fmtTime(cnc.sheets[s.number])}</span>
                  )}
                  {simProjectId && (
                    <Button size="sm" variant="outline" data-testid={`gcode-sim-btn-${s.number}`} onClick={() => setSim(s.number)} className="border-[#E67E22] text-[#E67E22] bg-transparent hover:bg-[#21252F]">
                      <PlayCircle className="w-4 h-4 mr-1" /> Simular corte
                    </Button>
                  )}
                </div>
              </div>
              <div className="rounded-lg bg-[#0F1115] keep-dark p-2">
                {edits ? (
                  <SheetEditor sheetNo={s.number} placed={edits[s.number] || s.placed} pieces={pieces}
                    onChange={(list) => setEdits((o) => ({ ...o, [s.number]: list }))} />
                ) : <SheetSvg sheet={s} pieces={pieces} />}
              </div>
              <div className="text-xs text-[#9CA3AF]">
                <span className="font-mono uppercase tracking-wider text-[10px] text-[#6B7280] mr-2">Sobras:</span>
                {s.sobras.length ? s.sobras.map((r, i) => <span key={i} className="mr-3 font-mono">S{i + 1} {r.w}x{r.h}</span>) : "nenhuma aproveitável"}
              </div>
            </div>
          ))}
          </div>
        </section>
      ))}
      {simProjectId && <GcodeSim open={sim !== null} onOpenChange={(o) => { if (!o) setSim(null); }} projectId={simProjectId} sheet={sim} />}
    </div>
  );
};
